import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { scenarioGenerationsAPI } from "../services/api/scenarioGenerations";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";

const STORAGE_KEY = "cqa_scenario_gen_tracker";
const POLL_MS = 3000;

// Only the scenario pages need live tracker updates. Outside these routes the
// tracker stays dormant — IDs persist in localStorage so polling resumes when
// the user navigates back, but no API calls fire from unrelated pages.
const SCENARIO_PATH_PREFIXES = [
  "/live-simulations/scenario",
  "/live-simulations/scenarios",
  "/live-simulations/generate",
];

const Ctx = createContext(null);

// Persist the list of tracked run ids (survives page reloads).
function loadIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveIds(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore quota errors
  }
}

export function ScenarioGenerationTrackerProvider({ children }) {
  const { isAuthenticated, isInitialized, currentWorkspace } = useAuth();
  const { showToast } = useToast();
  const { pathname } = useLocation();
  // Authenticated URLs are prefixed with the workspace UUID (/<uuid>/...),
  // so match anywhere in the path — startsWith fails against that prefix.
  const onScenarioPage = SCENARIO_PATH_PREFIXES.some((p) => pathname.includes(p));
  // `runs` is a map of { [id]: run } for every tracked id we've seen a response for.
  const [runs, setRuns] = useState({});
  // `trackedIds` is the authoritative list of ids we're polling.
  const [trackedIds, setTrackedIds] = useState(() => loadIds());
  const timersRef = useRef({});

  // Persist the id list whenever it changes.
  useEffect(() => {
    saveIds(trackedIds);
  }, [trackedIds]);

  const stopPolling = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  // Drop completed/failed ids from the persistent tracked list so a stale id
  // from a prior session doesn't trigger an unnecessary fetch on every page
  // reload. Active runs (pending/running) stay in localStorage so the dock
  // recovers state after a refresh while the job is still in flight.
  const retire = useCallback((id) => {
    setTrackedIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const startPolling = useCallback((id) => {
    const tick = async () => {
      try {
        // Scope the poll to the browsed workspace. Without it the backend
        // resolves the user's DEFAULT workspace and 404s on runs that belong to
        // any other one — which the catch below silently retires, so the run
        // just disappears from the dock with no error.
        const r = await scenarioGenerationsAPI.get(id, currentWorkspace?.id);
        setRuns((prev) => ({ ...prev, [id]: r }));
        if (r.status === "completed") {
          showToast(`Generated ${(r.drafts || []).length} scenarios — added to your list`, "success");
          stopPolling(id);
          retire(id);
          return;
        }
        if (r.status === "failed") {
          showToast(r.error_message || "Scenario generation failed", "error");
          stopPolling(id);
          retire(id);
          return;
        }
        timersRef.current[id] = setTimeout(tick, POLL_MS);
      } catch (e) {
        // If the run is 404 (deleted or never existed), drop it silently.
        if (e.response?.status === 404) {
          setTrackedIds((prev) => prev.filter((x) => x !== id));
          stopPolling(id);
          return;
        }
        // Transient error — retry after poll interval.
        timersRef.current[id] = setTimeout(tick, POLL_MS);
      }
    };
    tick();
  }, [stopPolling, retire, showToast, currentWorkspace]);

  // (Re)start polling when trackedIds changes — but only on scenario pages.
  // Outside scenario routes the tracker stays dormant: trackedIds persist in
  // localStorage so the dock can resume polling once the user returns. This
  // avoids the bug where every page in the app fired a /scenario_generations
  // fetch on mount because of leftover ids from a prior session.
  // Also skip entirely when unauthenticated — polling without a token 401s,
  // triggering the axios interceptor's hard redirect to /login which loops.
  useEffect(() => {
    if (!isInitialized || !isAuthenticated || !onScenarioPage) {
      // Stop any in-flight timers while unauth'd or off-page.
      Object.keys(timersRef.current).forEach((id) => clearTimeout(timersRef.current[id]));
      timersRef.current = {};
      return;
    }
    trackedIds.forEach((id) => {
      if (!timersRef.current[id]) startPolling(id);
    });
    // Stop pollers for ids no longer tracked.
    Object.keys(timersRef.current).forEach((id) => {
      if (!trackedIds.includes(Number(id)) && !trackedIds.includes(String(id))) {
        stopPolling(id);
      }
    });
    return () => {
      // On unmount or when leaving scenario routes, clear every timer.
      Object.keys(timersRef.current).forEach((id) => clearTimeout(timersRef.current[id]));
      timersRef.current = {};
    };
  }, [trackedIds, startPolling, stopPolling, isAuthenticated, isInitialized, onScenarioPage]);

  const track = useCallback((id) => {
    if (!id) return;
    setTrackedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const dismiss = useCallback((id) => {
    setTrackedIds((prev) => prev.filter((x) => x !== id));
    setRuns((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    stopPolling(id);
  }, [stopPolling]);

  // Visible runs = tracked ids that have at least one fetched response AND
  // aren't already completed/failed for > 10s (give a moment of celebration).
  const activeRuns = trackedIds
    .map((id) => runs[id])
    .filter(Boolean)
    .filter((r) => r.status === "pending" || r.status === "running");

  return (
    <Ctx.Provider value={{ track, dismiss, activeRuns }}>
      {children}
    </Ctx.Provider>
  );
}

export function useScenarioGenerationTracker() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useScenarioGenerationTracker must be used inside ScenarioGenerationTrackerProvider");
  return ctx;
}
